import { BiMessageSquareError, BiHistory } from "react-icons/bi";
import { BsBookmarks, BsCreditCard2Front, BsDashCircleDotted, BsTerminal } from "react-icons/bs";
import { FaRegBuilding } from "react-icons/fa";
import { FiUserCheck, FiUsers, FiGlobe } from "react-icons/fi";
import { GrConfigure } from "react-icons/gr";
import { HiOutlineTicket } from "react-icons/hi";
import { IoKeypadOutline } from "react-icons/io5";
import { MdOutlineSubject } from "react-icons/md";
import { RiProductHuntLine } from "react-icons/ri";
import { BsCreditCard } from "react-icons/bs";
import useAuthStore from "store/authStore";
import { Permission } from "utils/server-constants/permissions";
import { Role } from "utils/server-constants/roles";

const useMenuItems = () => {
	const { hasRoles, hasPermissions } = useAuthStore();
	const ICON_SIZE = 18;
	let adminDashboard = "/admin-dashboard";

	if (hasRoles([Role.SeniorPublisher, Role.Publisher])) {
		adminDashboard = "/publisher-dashboard";
	} else if (hasRoles([Role.Institution, Role.Educator])) {
		adminDashboard = "/institution-dashboard";
	} else if (hasRoles([Role.Ba])) {
		adminDashboard = "/ba-dashboard";
	}

	const menu = [
		{
			type: "group",
			id: "overview",
			label: "Overview",
			icon: <BsDashCircleDotted size={ICON_SIZE} />,
			children: [
				{
					type: "link",
					to: adminDashboard,
					label: "Dashboard",
					icon: <BsDashCircleDotted size={ICON_SIZE} />,
					show: hasRoles([
						Role.SuperAdmin,
						Role.Admin,
						Role.SeniorPublisher,
						Role.Publisher,
						Role.Institution,
						Role.Educator,
						Role.Ba,
					]),
				},
				{
					type: "link",
					to: "/ba/client-payments",
					label: "Statement",
					icon: <RiProductHuntLine size={ICON_SIZE} />,
					show: hasRoles([Role.Ba]),
				},
			],
		},
		{
			type: "group",
			id: "books",
			label: "Books",
			icon: <BsBookmarks size={ICON_SIZE} />,
			children: [
				{
					type: "link",
					to: "/books/admin/all",
					label: "Books",
					icon: <BsBookmarks size={ICON_SIZE} />,
					show:
						hasRoles([Role.SuperAdmin, Role.Admin, Role.SeniorPublisher, Role.Publisher]) &&
						hasPermissions([Permission.ViewBooks]),
				},
				{
					type: "link",
					to: "/admin/physical-books",
					label: "Physical books",
					icon: <BsBookmarks size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.ViewBooks]),
				},
				{
					type: "link",
					to: "/books/institution",
					label: "Purchased Books",
					icon: <BsBookmarks size={ICON_SIZE} />,
					show:
						hasRoles([Role.Institution, Role.Educator]) && hasPermissions([Permission.ViewBooks]),
				},
				{
					type: "link",
					to: "/books/institution-picks",
					label: "Subscription Picks",
					icon: <BsBookmarks size={ICON_SIZE} />,
					show:
						hasRoles([Role.Institution, Role.Educator]) && hasPermissions([Permission.ViewBooks]),
				},
				{
					type: "link",
					to: "/books/publisher/purchased",
					label: "Sold books",
					icon: <BsBookmarks size={ICON_SIZE} />,
					show:
						hasRoles([Role.SeniorPublisher, Role.Publisher]) &&
						hasPermissions([Permission.ViewSoldBooks]),
				},
				{
					type: "link",
					to: "/admin/publishers/sold-books-export",
					label: "Publisher sales export",
					icon: <BsBookmarks size={ICON_SIZE} />,
					show:
						hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.ViewSoldBooks]),
				},
				{
					type: "link",
					to: "/books/admin/genres-and-categories",
					label: "Book categories",
					icon: <BsBookmarks size={ICON_SIZE} />,
					show:
						hasRoles([Role.SuperAdmin, Role.Admin]) &&
						hasPermissions([Permission.ViewCategory, Permission.ViewGenre]),
				},
			],
		},
		{
			type: "group",
			id: "users-and-orgs",
			label: "Users & Orgs",
			icon: <FiUsers size={ICON_SIZE} />,
			children: [
				{
					type: "link",
					to: "/users/admin/all",
					label: "Users",
					icon: <FiUsers size={ICON_SIZE} />,
					show:
						hasRoles([
							Role.SuperAdmin,
							Role.Admin,
							Role.SeniorPublisher,
							Role.Publisher,
							Role.Institution,
							Role.Educator,
						]) && hasPermissions([Permission.ViewUser]),
				},
				{
					type: "link",
					to: "/brand-ambassadors",
					label: "Brand Ambassadors",
					icon: <FiUserCheck size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.ViewUser]),
				},
				{
					type: "link",
					to: "/publishers",
					label: "Publishers",
					icon: <FaRegBuilding size={ICON_SIZE} />,
					show:
						hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.ViewPublisher]),
				},
				{
					type: "link",
					to: "/institutions",
					label: "Institutions",
					icon: <FaRegBuilding size={ICON_SIZE} />,
					show:
						hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.ViewInstitution]),
				},
				{
					type: "link",
					to: "/subject-groups",
					label: "Subject groups",
					icon: <MdOutlineSubject size={ICON_SIZE} />,
					show:
						hasRoles([Role.SuperAdmin, Role.Admin, Role.Institution, Role.Educator]) &&
						hasPermissions([Permission.ViewSubjectGroup]),
				},
				{
					type: "link",
					to: "/roles-and-permissions",
					label: "Roles/permissions",
					icon: <IoKeypadOutline size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.ViewRole]),
				},
			],
		},
		{
			type: "group",
			id: "commerce",
			label: "Commerce",
			icon: <HiOutlineTicket size={ICON_SIZE} />,
			children: [
				{
					type: "link",
					to: "/vouchers",
					label: "Vouchers",
					icon: <HiOutlineTicket size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.ViewVoucher]),
				},
				{
					type: "link",
					to: "/admin/subscription-plans",
					label: "Subscription Plans",
					icon: <BsCreditCard size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.ViewVoucher]),
				},
				{
					type: "link",
					to: "/orders",
					label: "Orders",
					icon: <RiProductHuntLine size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.ViewOrder]),
				},
				{
					type: "link",
					to: "/orders-to-assign",
					label: "Assign orders",
					icon: <RiProductHuntLine size={ICON_SIZE} />,
					show:
						hasRoles([Role.SuperAdmin, Role.Admin]) &&
						hasPermissions([Permission.ViewOrder, Permission.AdminCourier]),
				},
				{
					type: "link",
					to: "/pay-publisher",
					label: "Publisher payouts",
					icon: <HiOutlineTicket size={ICON_SIZE} />,
					show:
						hasRoles([Role.SuperAdmin, Role.Admin]) &&
						hasPermissions([Permission.RecordPubPayouts]),
				},
				{
					type: "link",
					to: "/pay-brand-ambassador",
					label: "BA payouts",
					icon: <BsCreditCard2Front size={ICON_SIZE} />,
					show:
						hasRoles([Role.SuperAdmin, Role.Admin]) && hasPermissions([Permission.RecordBaPayouts]),
				},
			],
		},
		{
			type: "group",
			id: "system",
			label: "System",
			icon: <GrConfigure size={ICON_SIZE} />,
			children: [
				{
					type: "link",
					to: "/admin/geo-restrictions",
					label: "Geo-Restrictions",
					icon: <FiGlobe size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin, Role.Admin, Role.SeniorPublisher, Role.Publisher]),
				},
				{
					type: "link",
					to: "/error-report",
					label: "Error report",
					icon: <BiMessageSquareError size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin]) && hasPermissions([Permission.ViewErrors]),
				},
				{
					type: "link",
					to: "/audit-trail",
					label: "Audit trail",
					icon: <BiHistory size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin]),
				},
				{
					type: "link",
					to: "/command-center",
					label: "Command center",
					icon: <BsTerminal size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin]) && hasPermissions([Permission.RunCli]),
				},
				{
					type: "link",
					to: "/configurations",
					label: "Configs",
					icon: <GrConfigure size={ICON_SIZE} />,
					show: hasRoles([Role.SuperAdmin]),
				},
			],
		},
	];

	return menu
		.map((item) => {
			if (item.type !== "group") {
				if (!item.show) {
					return null;
				}

				const { show, ...visibleItem } = item;
				return visibleItem;
			}

			const visibleChildren = item.children
				.filter((child) => child.show)
				.map(({ show, ...child }) => child);

			if (visibleChildren.length === 0) {
				return null;
			}

			return {
				...item,
				children: visibleChildren,
			};
		})
		.filter(Boolean);
};

export default useMenuItems;
